import { lazy, type ComponentType, type ReactNode } from 'react';
import { createBrowserRouter, Navigate } from 'react-router-dom';
import { AdminLayout } from '@/layouts/AdminLayout';
import { AuthLayout } from '@/layouts/AuthLayout';
import { ProtectedRoute } from '@/auth/ProtectedRoute';
import { PublicOnlyRoute } from '@/auth/PublicOnlyRoute';
import { PermissionGuard } from '@/auth/PermissionGuard';
import type { PermissionKey } from '@/types/permissions';
import { paths } from './paths';

// Auth pages are small and needed immediately — import eagerly.
import { LoginPage } from '@/features/auth/pages/LoginPage';
import { ForgotPasswordPage } from '@/features/auth/pages/ForgotPasswordPage';
import { ResetPasswordPage } from '@/features/auth/pages/ResetPasswordPage';
import { VerifyEmailPage } from '@/features/auth/pages/VerifyEmailPage';
import { NotFoundPage } from '@/features/errors/NotFoundPage';
import { ForbiddenPage } from '@/features/errors/ForbiddenPage';

// Heavier authenticated pages are code-split (spec §31).
const lazyNamed = <T extends ComponentType<unknown>>(
  loader: () => Promise<Record<string, T>>,
  name: string,
) =>
  lazy(async () => {
    const module = await loader();
    return { default: module[name] };
  });

const DashboardPage = lazyNamed(
  () => import('@/features/dashboard/pages/DashboardPage'),
  'DashboardPage',
);
const ProfilePage = lazyNamed(() => import('@/features/account/pages/ProfilePage'), 'ProfilePage');
const SecurityPage = lazyNamed(
  () => import('@/features/account/pages/SecurityPage'),
  'SecurityPage',
);
const SessionsPage = lazyNamed(
  () => import('@/features/account/pages/SessionsPage'),
  'SessionsPage',
);
const MediaLibraryPage = lazyNamed(
  () => import('@/features/media/pages/MediaLibraryPage'),
  'MediaLibraryPage',
);
const NotificationsPage = lazyNamed(
  () => import('@/features/notifications/pages/NotificationsPage'),
  'NotificationsPage',
);

// Phase 3 — Website content
const PagesListPage = lazyNamed(
  () => import('@/features/pages/pages/PagesListPage'),
  'PagesListPage',
);
const PageBuilderPage = lazyNamed(
  () => import('@/features/page-builder/pages/PageBuilderPage'),
  'PageBuilderPage',
);
const NavigationPage = lazyNamed(
  () => import('@/features/navigation/pages/NavigationPage'),
  'NavigationPage',
);
const WebsiteSettingsPage = lazyNamed(
  () => import('@/features/website-settings/pages/WebsiteSettingsPage'),
  'WebsiteSettingsPage',
);
const SeoPage = lazyNamed(() => import('@/features/seo/pages/SeoPage'), 'SeoPage');

// Phase 4 — Marketing content
const ServicesPage = lazyNamed(() => import('@/features/content/modules/pages'), 'ServicesPage');
const IndustriesPage = lazyNamed(
  () => import('@/features/content/modules/pages'),
  'IndustriesPage',
);
const SolutionsPage = lazyNamed(() => import('@/features/content/modules/pages'), 'SolutionsPage');
const CaseStudiesPage = lazyNamed(
  () => import('@/features/content/modules/pages'),
  'CaseStudiesPage',
);
const PortfolioPage = lazyNamed(() => import('@/features/content/modules/pages'), 'PortfolioPage');
const TestimonialsPage = lazyNamed(
  () => import('@/features/content/modules/pages'),
  'TestimonialsPage',
);
const TeamPage = lazyNamed(() => import('@/features/content/modules/pages'), 'TeamPage');
const PricingPage = lazyNamed(() => import('@/features/pricing/PricingPage'), 'PricingPage');
const ReviewsPage = lazyNamed(() => import('@/features/reviews/pages/ReviewsPage'), 'ReviewsPage');

// Phase 5 — Editorial & intake
const BlogPage = lazyNamed(() => import('@/features/content/modules/pages5'), 'BlogPage');
const ResourcesPage = lazyNamed(() => import('@/features/content/modules/pages5'), 'ResourcesPage');
const ToolsPage = lazyNamed(() => import('@/features/content/modules/pages5'), 'ToolsPage');
const FaqsPage = lazyNamed(() => import('@/features/content/modules/pages5'), 'FaqsPage');
const CareersPage = lazyNamed(() => import('@/features/careers/CareersPage'), 'CareersPage');
const FormsListPage = lazyNamed(
  () => import('@/features/forms/pages/FormsListPage'),
  'FormsListPage',
);
const FormBuilderPage = lazyNamed(
  () => import('@/features/forms/pages/FormBuilderPage'),
  'FormBuilderPage',
);
const SubmissionsPage = lazyNamed(
  () => import('@/features/forms/pages/SubmissionsPage'),
  'SubmissionsPage',
);

// Phase 6 — Leads & CRM
const LeadsPage = lazyNamed(() => import('@/features/leads/pages/LeadsPage'), 'LeadsPage');
const CrmPage = lazyNamed(() => import('@/features/crm/pages/CrmPage'), 'CrmPage');

// Phase 7 — Growth
const AutomationsListPage = lazyNamed(
  () => import('@/features/automation/pages/AutomationsListPage'),
  'AutomationsListPage',
);
const AutomationBuilderPage = lazyNamed(
  () => import('@/features/automation/pages/AutomationBuilderPage'),
  'AutomationBuilderPage',
);
const EmailPage = lazyNamed(() => import('@/features/email/pages/EmailPage'), 'EmailPage');
const PopupsPage = lazyNamed(() => import('@/features/popups/pages/PopupsPage'), 'PopupsPage');
const BookingPage = lazyNamed(() => import('@/features/booking/pages/BookingPage'), 'BookingPage');
const AnalyticsPage = lazyNamed(
  () => import('@/features/analytics/pages/AnalyticsPage'),
  'AnalyticsPage',
);

// Phase 8 — Administration
const UsersPage = lazyNamed(() => import('@/features/users/pages/UsersPage'), 'UsersPage');
const RolesPage = lazyNamed(() => import('@/features/roles/pages/RolesPage'), 'RolesPage');
const PermissionsPage = lazyNamed(
  () => import('@/features/permissions/pages/PermissionsPage'),
  'PermissionsPage',
);
const AuditLogsPage = lazyNamed(
  () => import('@/features/audit/pages/AuditLogsPage'),
  'AuditLogsPage',
);
const IntegrationsPage = lazyNamed(
  () => import('@/features/integrations/pages/IntegrationsPage'),
  'IntegrationsPage',
);
const SystemSettingsPage = lazyNamed(
  () => import('@/features/settings/pages/SystemSettingsPage'),
  'SystemSettingsPage',
);

/** Wrap an element with a route-level permission guard. */
function guarded(permission: PermissionKey, element: ReactNode): ReactNode {
  return <PermissionGuard permission={permission}>{element}</PermissionGuard>;
}

export const router = createBrowserRouter([
  {
    element: (
      <PublicOnlyRoute>
        <AuthLayout />
      </PublicOnlyRoute>
    ),
    children: [
      { path: paths.login, element: <LoginPage /> },
      { path: paths.forgotPassword, element: <ForgotPasswordPage /> },
      { path: paths.resetPassword, element: <ResetPasswordPage /> },
      { path: paths.verifyEmail, element: <VerifyEmailPage /> },
    ],
  },
  {
    element: (
      <ProtectedRoute>
        <AdminLayout />
      </ProtectedRoute>
    ),
    children: [
      { index: true, element: <Navigate to={paths.dashboard} replace /> },
      { path: paths.dashboard, element: <DashboardPage /> },

      // Account
      { path: paths.profile, element: <ProfilePage /> },
      { path: paths.security, element: <SecurityPage /> },
      { path: paths.sessions, element: <SessionsPage /> },

      // Website
      { path: paths.pages, element: guarded('pages:view', <PagesListPage />) },
      { path: paths.pageBuilder, element: guarded('sections:view', <PageBuilderPage />) },
      { path: paths.navigation, element: guarded('navigation:view', <NavigationPage />) },
      {
        path: paths.websiteSettings,
        element: guarded('website-settings:view', <WebsiteSettingsPage />),
      },
      { path: paths.seo, element: guarded('seo:view', <SeoPage />) },
      { path: paths.media, element: guarded('media:view', <MediaLibraryPage />) },

      // Content
      { path: paths.services, element: guarded('services:view', <ServicesPage />) },
      { path: paths.industries, element: guarded('industries:view', <IndustriesPage />) },
      { path: paths.solutions, element: guarded('solutions:view', <SolutionsPage />) },
      { path: paths.caseStudies, element: guarded('case-studies:view', <CaseStudiesPage />) },
      { path: paths.portfolio, element: guarded('portfolio:view', <PortfolioPage />) },
      { path: paths.pricing, element: guarded('pricing:view', <PricingPage />) },
      { path: paths.testimonials, element: guarded('testimonials:view', <TestimonialsPage />) },
      { path: paths.team, element: guarded('team:view', <TeamPage />) },
      { path: paths.reviews, element: guarded('reviews:view', <ReviewsPage />) },
      { path: paths.blog, element: guarded('blog:view', <BlogPage />) },
      { path: paths.resources, element: guarded('resources:view', <ResourcesPage />) },
      { path: paths.tools, element: guarded('tools:view', <ToolsPage />) },
      { path: paths.faqs, element: guarded('faqs:view', <FaqsPage />) },
      { path: paths.careers, element: guarded('careers:view', <CareersPage />) },
      { path: paths.forms, element: guarded('forms:view', <FormsListPage />) },
      { path: paths.formBuilder, element: guarded('forms:view', <FormBuilderPage />) },
      {
        path: paths.submissions,
        element: guarded('form-submissions:view', <SubmissionsPage />),
      },

      // Acquisition
      { path: paths.leads, element: guarded('leads:view', <LeadsPage />) },
      { path: paths.crm, element: guarded('crm:view', <CrmPage />) },
      { path: paths.automations, element: guarded('automation:view', <AutomationsListPage />) },
      {
        path: paths.automationBuilder,
        element: guarded('automation:view', <AutomationBuilderPage />),
      },
      {
        path: paths.emailTemplates,
        element: guarded('email-templates:view', <EmailPage />),
      },
      { path: paths.popups, element: guarded('popups:view', <PopupsPage />) },
      { path: paths.booking, element: guarded('booking:view', <BookingPage />) },

      // Insights
      { path: paths.analytics, element: guarded('analytics:view', <AnalyticsPage />) },
      // Every authenticated user can see their own notifications (no extra permission).
      { path: paths.notifications, element: <NotificationsPage /> },

      // Administration
      { path: paths.users, element: guarded('users:view', <UsersPage />) },
      { path: paths.roles, element: guarded('roles:view', <RolesPage />) },
      { path: paths.permissions, element: guarded('permissions:view', <PermissionsPage />) },
      { path: paths.auditLogs, element: guarded('audit:view', <AuditLogsPage />) },
      { path: paths.integrations, element: guarded('integrations:view', <IntegrationsPage />) },
      { path: paths.systemSettings, element: guarded('settings:view', <SystemSettingsPage />) },

      { path: paths.forbidden, element: <ForbiddenPage /> },
    ],
  },
  { path: paths.notFound, element: <NotFoundPage /> },
  { path: '*', element: <NotFoundPage /> },
]);
